import React, { useEffect, useState } from 'react';
import { ChevronDown, Globe, Loader2, Wand2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { i18nAPI, pagesAPI } from '../services/api';
import { renderMarkdown } from '../services/markdown';
import { TARGET_LANGS } from './TranslationsEditor';

// Per-language title + markdown content of an information page, with an AI job per page
const PageTranslationsPanel = ({ form, setForm, pageId }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('en');
  const [preview, setPreview] = useState(false);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const i18n = form.i18n || {};
  const filled = TARGET_LANGS.filter(l => i18n[l.code]?.title && i18n[l.code]?.content).length;
  const setField = (key, value) => setForm(f => ({ ...f, i18n: { ...(f.i18n || {}), [tab]: { ...((f.i18n || {})[tab] || {}), [key]: value } } }));

  useEffect(() => {
    if (!job?.running) return undefined;
    const id = setInterval(async () => {
      try {
        const st = await i18nAPI.status();
        const j = st.jobs?.pages;
        setJob(j);
        if (j && !j.running) {
          clearInterval(id);
          const fresh = (await pagesAPI.adminList()).pages.find(p => p.id === pageId);
          if (fresh) setForm(f => ({ ...f, i18n: fresh.i18n || {} }));
          if (j.errors?.length) setError(j.errors.join(' ; '));
        }
      } catch { /* retry */ }
    }, 3000);
    return () => clearInterval(id);
  }, [job?.running, pageId, setForm]);

  const translate = async (overwrite) => {
    if (overwrite && !window.confirm('Remplacer les traductions existantes de cette page par une nouvelle version IA ?')) return;
    setError(null);
    try {
      const r = await i18nAPI.translatePages(pageId, overwrite);
      if (!r.started) { setError(r.message); return; }
      setJob(r.job);
    } catch (e) { setError(e.response?.data?.detail || 'Traduction impossible'); }
  };

  return (
    <div className="border border-gray-200 rounded-lg" data-testid="page-translations">
      <button type="button" data-testid="page-translations-toggle" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Globe size={15} className="text-violet-600" /> Traductions (EN / DE / ES / IT)</span>
        <span className="flex items-center gap-2">
          <span data-testid="page-translations-count" className={`text-xs font-bold px-2 py-0.5 rounded-full ${filled === 4 ? 'bg-sky-100 text-sky-700' : filled ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{filled}/4 langues</span>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
          <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-xs text-gray-600 flex-1">{pageId ? 'Traduit le titre et le contenu Markdown à partir du français (les données société {{…}} sont conservées). Enregistré automatiquement, à relire.' : 'Créez d\'abord la page pour lancer la traduction IA.'}</p>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" data-testid="page-translate-button" onClick={() => translate(false)} disabled={!pageId || job?.running} className="border-violet-400 text-violet-700 hover:bg-violet-100">
                {job?.running ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Wand2 size={13} className="mr-1" />} {job?.running ? 'Traduction en cours…' : 'Traduire (IA)'}
              </Button>
              {filled > 0 && !job?.running && <Button type="button" size="sm" variant="ghost" data-testid="page-retranslate-button" onClick={() => translate(true)} disabled={!pageId} className="text-xs text-gray-500">Regénérer</Button>}
            </div>
          </div>
          {error && <p className="text-xs text-red-600" data-testid="page-translations-error">{error}</p>}
          <div className="flex items-center justify-between border-b">
            <div className="flex" role="tablist">
              {TARGET_LANGS.map(l => (
                <button key={l.code} type="button" role="tab" onClick={() => setTab(l.code)} data-testid={`page-translations-tab-${l.code}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border-b-2 -mb-px transition-colors ${tab === l.code ? 'border-violet-500 text-violet-700 font-semibold' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                  <img src={`https://flagcdn.com/w20/${l.flag}.png`} alt="" className="w-4 h-3 rounded-sm no-placeholder" /> {l.label}
                  {i18n[l.code]?.content && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                </button>
              ))}
            </div>
            <button type="button" data-testid="page-translations-preview-toggle" onClick={() => setPreview(v => !v)} className={`text-xs px-2 py-1 rounded border ${preview ? 'bg-orange-500 text-white border-orange-500' : 'text-gray-600 border-gray-300 hover:border-orange-400'}`}>{preview ? 'Éditer' : 'Aperçu'}</button>
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Titre <span className="font-normal text-gray-400">· FR : {form.title}</span></span>
            <Input value={i18n[tab]?.title || ''} onChange={(e) => setField('title', e.target.value)} maxLength={120} className="h-8 text-sm" data-testid={`page-translations-${tab}-title`} />
          </label>
          {preview ? (
            <div data-testid={`page-translations-${tab}-preview`} className="prose-legal border border-gray-200 rounded-md p-4 min-h-[200px] text-sm text-gray-700 bg-gray-50" dangerouslySetInnerHTML={{ __html: renderMarkdown(i18n[tab]?.content || '') }} />
          ) : (
            <Textarea value={i18n[tab]?.content || ''} onChange={(e) => setField('content', e.target.value)} rows={12} maxLength={30000} className="font-mono text-sm" placeholder="Contenu traduit (Markdown)" data-testid={`page-translations-${tab}-content`} />
          )}
        </div>
      )}
    </div>
  );
};

export default PageTranslationsPanel;
